import React, { useCallback, useContext, useMemo, useState } from 'react';
import { AvailableDimension, AvailableDomain, AvailableHypercube, AvailableMember, DimensionLabels } from 'app/api/tag-queries';
import { TagContext } from 'app/state/contexts/TagContext';
import { i18n } from 'app/utils/i18n';

import '../taxonomy/Taxonomy.scss';
import './DimensionTree.scss';

function pickLabel(labels: DimensionLabels, lang: string, id: string): string {
  const locale = (labels as unknown as Record<string, string | undefined>)[lang];
  if (locale) return locale;
  if (labels.fallback) return labels.fallback;
  return id;
}

// -------- Props --------

export interface DimensionTreeProps {
  hypercubes: AvailableHypercube[];
  selectedMemberId: string | null;
  onSelect: (selection: { hypercube: AvailableHypercube; dimension: AvailableDimension; member: AvailableMember }) => void;
  onAddMember?: (axis: AvailableDimension, hypercube: AvailableHypercube) => void;
  onEditMember?: (member: AvailableMember, axis: AvailableDimension, hypercube: AvailableHypercube) => void;
  onDeleteMember?: (member: AvailableMember) => void;
  onAddTypedValue?: (selection: { hypercube: AvailableHypercube; dimension: AvailableDimension; value: string }) => void;
}

// -------- Icon helper --------

type NodeKind = 'hypercube' | 'dimension' | 'domain' | 'member';

function kindIcon(kind: NodeKind): React.ReactNode {
  switch (kind) {
    case 'hypercube':
      return <i className="fa-regular fa-table" />;
    case 'dimension':
      return <i className="fa-regular fa-arrows-left-right-to-line" />;
    case 'domain':
      return <i className="fa-regular fa-hexagon-nodes" />;
    case 'member':
      return <i className="fa-regular fa-circle-nodes" />;
  }
}

function kindLabelKey(kind: NodeKind): string {
  switch (kind) {
    case 'hypercube':
      return 'tag.dimensions.kind.table';
    case 'dimension':
      return 'tag.dimensions.kind.axis';
    case 'domain':
      return 'tag.dimensions.kind.domain';
    case 'member':
      return 'tag.dimensions.kind.member';
  }
}

const CaretToggle: React.FC<{ open: boolean; onToggle: (e: React.MouseEvent) => void }> = ({ open, onToggle }) => (
  <span
    role="button"
    tabIndex={0}
    aria-expanded={open}
    className={`dimension-tree__caret dimension-tree__caret--inline${open ? '' : ' dimension-tree__caret--closed'}`}
    onClick={onToggle}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onToggle(e as unknown as React.MouseEvent);
      }
    }}
  >
    <i className="fa-solid fa-caret-down" />
  </span>
);

const CaretSpacer: React.FC = () => <span className="dimension-tree__caret-spacer" />;

// -------- Top-level tree --------

export const DimensionTree: React.FC<DimensionTreeProps> = ({
  hypercubes,
  selectedMemberId,
  onSelect,
  onAddMember,
  onEditMember,
  onDeleteMember,
  onAddTypedValue,
}) => {
  return (
    <>
      {hypercubes.map((hc, idx) => (
        <HypercubeAccordion
          key={`hc-${idx}-${hc.id}`}
          pathKey={`hc-${idx}`}
          hypercube={hc}
          selectedMemberId={selectedMemberId}
          onSelect={onSelect}
          onAddMember={onAddMember}
          onEditMember={onEditMember}
          onDeleteMember={onDeleteMember}
          onAddTypedValue={onAddTypedValue}
        />
      ))}
    </>
  );
};

// -------- Hypercube (outer collapsible) --------

interface HypercubeAccordionProps {
  hypercube: AvailableHypercube;
  pathKey: string;
  selectedMemberId: string | null;
  onSelect: DimensionTreeProps['onSelect'];
  onAddMember?: DimensionTreeProps['onAddMember'];
  onEditMember?: DimensionTreeProps['onEditMember'];
  onDeleteMember?: DimensionTreeProps['onDeleteMember'];
  onAddTypedValue?: DimensionTreeProps['onAddTypedValue'];
}

const HypercubeAccordion: React.FC<HypercubeAccordionProps> = ({
  hypercube,
  pathKey,
  selectedMemberId,
  onSelect,
  onAddMember,
  onEditMember,
  onDeleteMember,
  onAddTypedValue,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { tagLanguage } = useContext(TagContext);

  const label = useMemo(() => pickLabel(hypercube.labels, tagLanguage, hypercube.id), [hypercube.labels, tagLanguage, hypercube.id]);

  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  return (
    <div className="dimension-tree">
      <div className={`dimension-tree__header${isOpen ? ' dimension-tree__header--open' : ''}`} onClick={toggle}>
        <div className={`dimension-tree__caret${isOpen ? '' : ' dimension-tree__caret--closed'}`}>
          <i className="fa-solid fa-caret-down" />
        </div>
        <div className="dimension-tree__row-text">
          <span className="dimension-tree__kind">
            {i18n(kindLabelKey('hypercube'))}
            {hypercube.closed && (
              <span
                className="dimension-tree__pill dimension-tree__pill--closed"
                title={i18n('tag.dimensions.closed-tooltip')}
                aria-label={i18n('tag.dimensions.closed-tooltip')}
              >
                {i18n('tag.dimensions.closed-pill')}
              </span>
            )}
          </span>
          <span className="dimension-tree__label">{label || hypercube.id}</span>
          {hypercube.roleLabel && <span className="dimension-tree__role-label">{hypercube.roleLabel}</span>}
        </div>
      </div>

      {isOpen && (
        <div className="dimension-tree__children">
          {hypercube.dimensions.map((dim, i) => (
            <DimensionNode
              key={`${pathKey}-d-${i}-${dim.id}`}
              pathKey={`${pathKey}-d-${i}`}
              hypercube={hypercube}
              dimension={dim}
              selectedMemberId={selectedMemberId}
              onSelect={onSelect}
              onAddMember={onAddMember}
              onEditMember={onEditMember}
              onDeleteMember={onDeleteMember}
              onAddTypedValue={onAddTypedValue}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface DimensionNodeProps {
  hypercube: AvailableHypercube;
  dimension: AvailableDimension;
  pathKey: string;
  selectedMemberId: string | null;
  onSelect: DimensionTreeProps['onSelect'];
  onAddMember?: DimensionTreeProps['onAddMember'];
  onEditMember?: DimensionTreeProps['onEditMember'];
  onDeleteMember?: DimensionTreeProps['onDeleteMember'];
  onAddTypedValue?: DimensionTreeProps['onAddTypedValue'];
}

const DimensionNode: React.FC<DimensionNodeProps> = ({
  hypercube,
  dimension,
  pathKey,
  selectedMemberId,
  onSelect,
  onAddMember,
  onEditMember,
  onDeleteMember,
  onAddTypedValue,
}) => {
  const { tagLanguage } = useContext(TagContext);
  const label = useMemo(() => pickLabel(dimension.labels, tagLanguage, dimension.id), [dimension.labels, tagLanguage, dimension.id]);

  const isTyped = !!dimension.typedDomainRef;
  const [isOpen, setIsOpen] = useState(false);
  const hasContent = isTyped || dimension.domains.length > 0;
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  const handleAdd = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onAddMember?.(dimension, hypercube);
    },
    [dimension, hypercube, onAddMember]
  );

  return (
    <div className="pos-rel dimension-tree__nested">
      <div className="dimension-tree__row dimension-tree__row--unselectable" onClick={hasContent ? toggle : undefined}>
        {hasContent ? (
          <CaretToggle
            open={isOpen}
            onToggle={(e) => {
              e.stopPropagation();
              toggle();
            }}
          />
        ) : (
          <CaretSpacer />
        )}
        <span className="dimension-tree__icon">{kindIcon('dimension')}</span>
        <div className="dimension-tree__row-text">
          <span className="dimension-tree__kind">{i18n(kindLabelKey('dimension'))}</span>
          <span className="dimension-tree__label">{label}</span>
        </div>
        {!isTyped && onAddMember && (
          <button
            type="button"
            className="btn btn-primary btn-sm dimension-tree__add-member-btn"
            onClick={handleAdd}
            title={i18n('tag.dimensions.add-member.title')}
            aria-label={i18n('tag.dimensions.add-member.title')}
          >
            <i className="fa-solid fa-plus" />
          </button>
        )}
      </div>
      {isOpen &&
        (isTyped ? (
          <TypedAxisInput hypercube={hypercube} dimension={dimension} onAddTypedValue={onAddTypedValue} />
        ) : (
          <div className="dimension-tree__children dimension-tree__nested">
            {dimension.domains.map((domain, i) => (
              <DomainNode
                key={`${pathKey}-dom-${i}-${domain.id}`}
                pathKey={`${pathKey}-dom-${i}`}
                hypercube={hypercube}
                dimension={dimension}
                domain={domain}
                selectedMemberId={selectedMemberId}
                onSelect={onSelect}
                onEditMember={onEditMember}
                onDeleteMember={onDeleteMember}
              />
            ))}
          </div>
        ))}
    </div>
  );
};

// -------- Typed-axis input --------

interface TypedAxisInputProps {
  hypercube: AvailableHypercube;
  dimension: AvailableDimension;
  onAddTypedValue?: DimensionTreeProps['onAddTypedValue'];
}

function pickInputType(typedDomainRef: string | null): 'number' | 'text' {
  if (!typedDomainRef) return 'text';
  return /Decimal|Integer|Numeric/i.test(typedDomainRef) ? 'number' : 'text';
}

const TypedAxisInput: React.FC<TypedAxisInputProps> = ({ hypercube, dimension, onAddTypedValue }) => {
  const [value, setValue] = useState('');
  const inputType = pickInputType(dimension.typedDomainRef);

  const handleSave = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || !onAddTypedValue) return;
    onAddTypedValue({ hypercube, dimension, value: trimmed });
    setValue('');
  }, [value, onAddTypedValue, hypercube, dimension]);

  return (
    <div className="dimension-tree__children dimension-tree__nested dimension-tree__typed-input">
      <div className="dimension-tree__row dimension-tree__row--unselectable">
        <input
          type={inputType}
          className="form-control form-control-sm"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={i18n('tag.dimensions.typed-value-placeholder')}
          data-testid={`dim-typed-input-${dimension.id}`}
          aria-label={`${i18n('tag.dimensions.typed-value-placeholder')} — ${dimension.id}`}
        />
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={handleSave}
          disabled={value.trim().length === 0 || !onAddTypedValue}
          data-testid={`dim-typed-save-${dimension.id}`}
        >
          {i18n('tag.dimensions.typed-value-save')}
        </button>
      </div>
    </div>
  );
};

// -------- Domain — display-only, not selectable --------

interface DomainNodeProps {
  hypercube: AvailableHypercube;
  dimension: AvailableDimension;
  domain: AvailableDomain;
  pathKey: string;
  selectedMemberId: string | null;
  onSelect: DimensionTreeProps['onSelect'];
  onEditMember?: DimensionTreeProps['onEditMember'];
  onDeleteMember?: DimensionTreeProps['onDeleteMember'];
}

const DomainNode: React.FC<DomainNodeProps> = ({ hypercube, dimension, domain, pathKey, selectedMemberId, onSelect, onEditMember, onDeleteMember }) => {
  const { tagLanguage } = useContext(TagContext);
  const label = useMemo(() => pickLabel(domain.labels, tagLanguage, domain.id), [domain.labels, tagLanguage, domain.id]);

  const [isOpen, setIsOpen] = useState(false);
  const hasMembers = domain.members.children.length > 0;
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  if (!hasMembers && domain.members.usable !== false) {
    return (
      <MemberNode
        pathKey={`${pathKey}-root`}
        hypercube={hypercube}
        dimension={dimension}
        member={domain.members}
        selectedMemberId={selectedMemberId}
        onSelect={onSelect}
        onEditMember={onEditMember}
        onDeleteMember={onDeleteMember}
      />
    );
  }

  return (
    <div className="pos-rel">
      <div className="dimension-tree__row dimension-tree__row--unselectable" onClick={hasMembers ? toggle : undefined}>
        {hasMembers ? (
          <CaretToggle
            open={isOpen}
            onToggle={(e) => {
              e.stopPropagation();
              toggle();
            }}
          />
        ) : (
          <CaretSpacer />
        )}
        <span className="dimension-tree__icon">{kindIcon('domain')}</span>
        <div className="dimension-tree__row-text">
          <span className="dimension-tree__kind">{i18n(kindLabelKey('domain'))}</span>
          <span className="dimension-tree__label">{label}</span>
        </div>
      </div>
      {isOpen && (
        <div className="dimension-tree__children dimension-tree__nested">
          {domain.members.children.map((m, i) => (
            <MemberNode
              key={`${pathKey}-m-${i}-${m.id}`}
              pathKey={`${pathKey}-m-${i}`}
              hypercube={hypercube}
              dimension={dimension}
              member={m}
              selectedMemberId={selectedMemberId}
              onSelect={onSelect}
              onEditMember={onEditMember}
              onDeleteMember={onDeleteMember}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// -------- Member — selectable (unless usable=false) --------

interface MemberNodeProps {
  hypercube: AvailableHypercube;
  dimension: AvailableDimension;
  member: AvailableMember;
  pathKey: string;
  selectedMemberId: string | null;
  onSelect: DimensionTreeProps['onSelect'];
  onEditMember?: DimensionTreeProps['onEditMember'];
  onDeleteMember?: DimensionTreeProps['onDeleteMember'];
}

const MemberNode: React.FC<MemberNodeProps> = ({ hypercube, dimension, member, pathKey, selectedMemberId, onSelect, onEditMember, onDeleteMember }) => {
  const { tagLanguage } = useContext(TagContext);
  const label = useMemo(() => pickLabel(member.labels, tagLanguage, member.id), [member.labels, tagLanguage, member.id]);

  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = member.children.length > 0;
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  const isDefault = dimension.defaultMemberId !== null && member.id === dimension.defaultMemberId;
  const selectable = member.usable !== false && !isDefault;
  const isSelected = member.id === selectedMemberId;

  const handleClick = useCallback(() => {
    if (!selectable) return;
    onSelect({ hypercube, dimension, member });
  }, [selectable, hypercube, dimension, member, onSelect]);

  const rowClasses = [
    'dimension-tree__row',
    selectable ? 'dimension-tree__row--selectable' : 'dimension-tree__row--disabled',
    isSelected ? 'dimension-tree__row--selected' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const disabledTooltip = !selectable ? (isDefault ? i18n('tag.dimensions.default-member-not-selectable') : i18n('tag.dimensions.not-usable')) : undefined;

  return (
    <>
      <div
        className={rowClasses}
        onClick={handleClick}
        data-testid={`dim-member-${member.id}`}
        title={disabledTooltip}
        aria-disabled={!selectable || undefined}
      >
        {hasChildren ? (
          <CaretToggle
            open={isOpen}
            onToggle={(e) => {
              e.stopPropagation();
              toggle();
            }}
          />
        ) : (
          <CaretSpacer />
        )}
        <span className="dimension-tree__icon">{kindIcon('member')}</span>
        <div className="dimension-tree__row-text">
          <span className="dimension-tree__kind">
            {i18n(kindLabelKey('member'))}
            {isDefault && <span className="dimension-tree__pill dimension-tree__pill--default">{i18n('tag.dimensions.default-pill')}</span>}
            {member.isExtension && (
              <span className="taxonomy__tag taxonomy__tag--extension" role="status" aria-label={i18n('tag.extension')}>
                {i18n('tag.extension')}
              </span>
            )}
            {member.inUse && (
              <span className="taxonomy__tag taxonomy__tag--used" role="status" aria-label={i18n('tag.used')}>
                {i18n('tag.used')}
              </span>
            )}
          </span>
          <span className="dimension-tree__label">{label}</span>
        </div>
        {member.isExtension && (onEditMember || onDeleteMember) && (
          <span className="dimension-tree__member-actions">
            {onEditMember && (
              <button
                type="button"
                className="btn btn-link btn-sm dimension-tree__member-action-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditMember(member, dimension, hypercube);
                }}
                title={i18n('tag.dimensions.edit-member.title')}
                aria-label={i18n('tag.dimensions.edit-member.title')}
              >
                <i className="fa-solid fa-pencil" />
              </button>
            )}
            {onDeleteMember && (
              <button
                type="button"
                className="btn btn-link btn-sm dimension-tree__member-action-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteMember(member);
                }}
                title={i18n('tag.dimensions.delete-member.title')}
                aria-label={i18n('tag.dimensions.delete-member.title')}
              >
                <i className="fa-solid fa-trash-can" />
              </button>
            )}
          </span>
        )}
      </div>
      {hasChildren && isOpen && (
        <div className="dimension-tree__children dimension-tree__nested">
          {member.children.map((child, i) => (
            <MemberNode
              key={`${pathKey}-c-${i}-${child.id}`}
              pathKey={`${pathKey}-c-${i}`}
              hypercube={hypercube}
              dimension={dimension}
              member={child}
              selectedMemberId={selectedMemberId}
              onSelect={onSelect}
              onEditMember={onEditMember}
              onDeleteMember={onDeleteMember}
            />
          ))}
        </div>
      )}
    </>
  );
};
